import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AppNotificationService } from '../../services/app-notification.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { QueryHandlerResponse } from '../../models/responseModels/QueryHandlerResponse';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CaseViewRoutingService } from '../../services/case-view-routing.service';
import { filter } from 'rxjs/operators';
import { ApplicationNotification } from '../../../root/models/application-notification.model';
import { NotificationCommand } from '../../models/commandModels/NotificationCommand';
import { AppUserManagementService } from '../../../app-user-management/service/app-user-management.service';
import { PersonForUser } from '../../../app-user-management/model/user-access-management.model';
import { CommonService } from '../../services/common.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { SignalrNotificationService } from '../../standalone-packages/ecap3/signalr-notification';
import { UserInfoService } from '../../standalone-packages/ecap3/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '../../services/toast-message.service';

@Component({
    selector: 'app-service-notification',
    templateUrl: './app-service-notification.component.html',
    styleUrls: ['./app-service-notification.component.scss'],
    standalone: false
})
export class AppServiceNotificationComponent implements OnInit, OnDestroy {
    private destroy$: Subject<boolean> = new Subject<boolean>();
    notificationServiceInfos: ApplicationNotification[] = [];
    userIds: any[] = [];
    personInfos: PersonForUser[] = [];
    @Output() applicationNotificationsEvent = new EventEmitter<ApplicationNotification[]>();
    isEnabledMoreBtn: boolean = false;
    clearAllBtnEnabled: boolean = false;
    isEnabledMarkAsReadAllBtn: boolean = false;
    isLoadingNotification: boolean = false;
    @ViewChild('moreMenuTrigger') moreMenuTrigger!: MatMenuTrigger;

    constructor(
        private router: Router,
        private readonly appNotificationService: AppNotificationService,
        private readonly appUserManagementComponent: AppUserManagementService,
        private readonly commonService: CommonService,
        private readonly signalrNotificationService: SignalrNotificationService,
        private readonly userInfoService: UserInfoService,
        private readonly toastMessageService: ToastMessageService,
        private readonly translateService: TranslateService,
        private caseViewRoutingService: CaseViewRoutingService
    ) {}

    ngOnInit(): void {
        // update notifications on route change
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(event => event instanceof NavigationStart)
            )
            .subscribe(() => {
                this.getApplicationNotifications();
            });

        this.getApplicationNotifications();
        this.commonService.isPublishNewNotification$.pipe(takeUntil(this.destroy$)).subscribe(value => {
            if (value) {
                this.getApplicationNotifications();
            }
        });
        this.subscribeToApplicationNotifications();
    }

    private getApplicationNotifications(): void {
        this.isLoadingNotification = true;
        this.appNotificationService
            .getApplicationNotifications()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: QueryHandlerResponse<null, ApplicationNotification[]>) => {
                    this.notificationServiceInfos = response.Results;
                    response.Results.forEach(item => {
                        if (item.CreatedBy) {
                            this.userIds.push(item.CreatedBy);
                        }
                    });
                    // this.getPersonInfoByUserIds();
                    this.applicationNotificationsEvent.emit(this.notificationServiceInfos);
                },
                complete: () => {
                    this.isEnabledMoreBtn = this.notificationServiceInfos.length > 0;
                    this.checkPostActionBtnStatusWithLoaderEnd();
                }
            });
    }

    public onChangeMarkStatus(applicationNotification: ApplicationNotification): void {
        const payload: NotificationCommand = {
            NotificationId: applicationNotification.ItemId,
            IsRead: applicationNotification.IsRead
        };
        this.appNotificationService
            .updateNotificationMarkStatus(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                if (response.Result) {
                    this.notificationServiceInfos.map(notification => {
                        if (notification.ItemId === applicationNotification.ItemId) {
                            notification.IsRead = applicationNotification.IsRead;
                        }
                        return notification;
                    });
                    this.updateAndEmitNotificationsStatus(this.notificationServiceInfos);
                    this.checkPostActionBtnStatus();
                }
            });
    }

    public onSelectNotification(event: any, applicationNotification: ApplicationNotification): void {
        if (!applicationNotification.IsRead) {
            applicationNotification.IsRead = !applicationNotification.IsRead;
            this.onChangeMarkStatus(applicationNotification);
        }
        if (applicationNotification?.Payload?.CaseId) {
            const route = this.caseViewRoutingService.getDetailsRoute(applicationNotification.Payload.CaseId);
            const url = this.router.serializeUrl(
                this.router.createUrlTree(route)
            );
            window.open(url, '_blank');
        }
    }

    private updateAndEmitNotificationsStatus(notificationServiceInfos: ApplicationNotification[]): void {
        this.applicationNotificationsEvent.emit(this.notificationServiceInfos);
    }

    private getPersonInfoByUserIds(): void {
        if (this.userIds.length > 0) {
            this.appUserManagementComponent
                .getPersonByUserIds(this.userIds)
                .pipe(takeUntil(this.destroy$))
                .subscribe(response => {
                    this.personInfos = response;
                });
        }
    }

    public onClearAllNotifications(event: any): void {
        this.isLoadingNotification = true;
        event.stopPropagation();
        const payload: NotificationCommand = {
            NotificationId: 'DeleteAll',
            IsRead: null
        };
        this.appNotificationService
            .clearAllNotification(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (response.Result) {
                        this.getApplicationNotifications();
                    }
                },
                complete: () => {
                    if (this.moreMenuTrigger) {
                        this.moreMenuTrigger.closeMenu();
                    }
                }
            });
    }

    public onClickMarkAsAllRead(event: any): void {
        this.isLoadingNotification = true;
        event.stopPropagation();
        const payload: NotificationCommand = {
            NotificationId: 'MarkAllAsRead',
            IsRead: true
        };
        this.appNotificationService
            .updateNotificationMarkStatus(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (response.Result) {
                        this.notificationServiceInfos.map(notification => {
                            notification.IsRead = true;
                            return notification;
                        });
                        this.updateAndEmitNotificationsStatus(this.notificationServiceInfos);
                    }
                },
                complete: () => {
                    if (this.moreMenuTrigger) {
                        this.moreMenuTrigger.closeMenu();
                    }
                    this.checkPostActionBtnStatusWithLoaderEnd();
                }
            });
    }

    private checkPostActionBtnStatusWithLoaderEnd(): void {
        this.checkPostActionBtnStatus();
        this.isLoadingNotification = false;
    }

    private checkPostActionBtnStatus(): void {
        this.isEnabledMarkAsReadAllBtn = this.notificationServiceInfos.some(notification => !notification.IsRead);
        this.clearAllBtnEnabled = this.notificationServiceInfos.some(notification => notification.IsRead);
        this.isLoadingNotification = false;
    }

    private subscribeToApplicationNotifications(): void {
        const userInfo = this.userInfoService.getUserInfo();
        if (userInfo?.UserId) {
            this.signalrNotificationService.subscribeSingle(
                'application-notification',
                userInfo.UserId,
                'application-notification',
                this.handleApplicationNotification.bind(this)
            );
        }
    }

    private handleApplicationNotification(_: any): void {
        try {
            this.toastMessageService.info(this.translateService.instant('NOTIFICATION.YOU_HAVE_NEW_NOTIFICATION'));
            this.getApplicationNotifications();
        } catch (error) {
            console.error('Error parsing application notification:', error);
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
